<template>
  <div class="delivery-performance">
    <div class="delivery-performance__cards">
      <div class="delivery-performance__deadline-kpis">
        <KpiCard
          v-for="card in kpis.slice(0, 2)"
          :key="card.title"
          :title="card.title"
          :value="card.value"
          :complement="card.complement"
          :complement-variant="card.complementVariant"
          :description="card.description"
          :value-color="card.valueColor"
          :positive="card.positive"
          :negative="card.negative"
        />
      </div>
      <KpiCard
        :title="kpis[2].title"
        :info="kpis[2].info"
        :value="kpis[2].value"
        :complement="kpis[2].complement"
        :complement-variant="kpis[2].complementVariant"
        :description="kpis[2].description"
        :value-color="kpis[2].valueColor"
        :positive="kpis[2].positive"
        :negative="kpis[2].negative"
      />
    </div>

    <div class="delivery-performance__grid delivery-performance__grid--top">
      <HorizontalBarChart
        title="Entregas vs. Prazos"
        :data="deliveriesByDeadline"
        unit=""
        :colors="colors"
        :loading="loading"
        show-real-value
      />
      <StackedBarChart
        title="Evolução do Tempo Médio por Etapas"
        :loading="loading"
        :categories="months"
        :series="stages"
        reference-line-name="Tempo Médio Atual"
        reference-line-tooltip-name="Tempo Médio Total"
        :reference-line-value="referenceLineValue"
      />
    </div>

    <div class="delivery-performance__panel">
      <div class="delivery-performance__panel-title">Tempo Médio: NTO por Exames</div>
      <BiChartEmpty v-if="!loading && !hasNtoData" />
      <template v-else>
        <div class="delivery-performance__filters">
          <button
            v-for="(filter, index) in ntoFilters"
            :key="filter"
            type="button"
            :class="{
              'delivery-performance__filter--active': index === selectedNtoIndex,
            }"
            @click="selectedNtoIndex = index"
          >
            {{ filter }}
          </button>
        </div>
        <div
          class="delivery-performance__grid delivery-performance__grid--detail delivery-performance__grid--average-time"
        >
          <LineChart
            :loading="loading"
            :categories="months"
            :values="ntoAverageTime"
            series-name="Tempo Médio"
            :tooltip-subtitle="ntoFilters[selectedNtoIndex]"
            :value-formatter="formatHours"
            :show-target="false"
            :show-background="false"
          />
          <Top5List
            :loading="loading"
            :show-title="false"
            :data="topNtoExams"
            :columns="averageTimeColumns"
            sort-key="sortValue"
            value-key="averageHours"
          />
        </div>
      </template>
    </div>

    <div class="delivery-performance__panel">
      <div class="delivery-performance__panel-title">
        Tempo Médio: Especialidade por Exames
      </div>
      <BiChartEmpty v-if="!loading && !hasSpecialtyData" />
      <template v-else>
        <div class="delivery-performance__filters">
          <button
            v-for="(filter, index) in specialtyFilters"
            :key="filter"
            type="button"
            :class="{
              'delivery-performance__filter--active': index === selectedSpecialtyIndex,
            }"
            @click="selectedSpecialtyIndex = index"
          >
            {{ filter }}
          </button>
        </div>
        <div
          class="delivery-performance__grid delivery-performance__grid--detail delivery-performance__grid--average-time"
        >
          <LineChart
            :loading="loading"
            :categories="months"
            :values="specialtyAverageTime"
            series-name="Tempo Médio"
            :tooltip-subtitle="selectedSpecialtyName"
            :value-formatter="formatHours"
            :show-target="false"
            :show-background="false"
          />
          <Top5List
            :loading="loading"
            :show-title="false"
            :data="topSpecialtyExams"
            :columns="averageTimeColumns"
            sort-key="sortValue"
            value-key="averageHours"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import KpiCard from '../charts/KpiCard.vue'
import HorizontalBarChart from '../charts/HorizontalBarChart.vue'
import StackedBarChart from '../charts/StackedBarChart.vue'
import LineChart from '../charts/LineChart.vue'
import Top5List from '../charts/Top5List.vue'
import biDeliveryPerformance from '../../../api/biDeliveryPerformance'
import BiChartEmpty from '../charts/BiChartEmpty.vue'
import { hasChartData } from '../charts/chartData'

const COLORS = [
  'rgba(34, 114, 58, 1)',
  'rgba(45, 168, 83, 1)',
  'rgba(105, 199, 86, 1)',
  'rgba(255, 191, 0, 1)',
  'rgba(218, 51, 71, 1)',
]
const DEADLINE_LABELS = {
  EARLY_MORE_48H: 'Adiantado mais de 48h',
  EARLY_24_48H: 'Adiantado em 24-48h',
  EARLY_UP_TO_24H: 'Adiantado em até 24h',
  ON_TIME: 'No prazo',
  LATE: 'Atrasado',
}
const DEADLINE_COLORS = {
  EARLY_MORE_48H: COLORS[0],
  EARLY_24_48H: COLORS[1],
  EARLY_UP_TO_24H: COLORS[2],
  ON_TIME: COLORS[3],
  LATE: COLORS[4],
}
const MONTHS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
]

export default {
  name: 'DeliveryPerformance',
  components: {
    KpiCard,
    HorizontalBarChart,
    StackedBarChart,
    LineChart,
    Top5List,
    BiChartEmpty,
  },
  props: {
    period: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      deliveryPerformanceResponse: null,
      periods: [],
      referenceLineValue: 0,
      colors: COLORS,
      kpis: [
        {
          title: 'No prazo ou Adiantados',
          value: '--',
          positive: true,
        },
        {
          title: 'Atrasado',
          value: '--',
          negative: true,
        },
        {
          title: 'Tempo Médio Total',
          value: '--',
          valueColor: '#7F1A54',
          info: 'Considera a soma do Tempo Médio Logístico e do Tempo Médio de Análise.',
        },
      ],
      months: [],
      deliveriesByDeadline: [],
      stages: [],
      ntoFilters: [],
      specialtyFilters: [],
      selectedNtoIndex: 0,
      selectedSpecialtyIndex: 0,
      ntoAverageTimeByFilter: [],
      specialtyAverageTimeByFilter: [],
      topNtoExamsByFilter: [],
      topSpecialtyExamsByFilter: [],
      averageTimeColumns: [
        { key: 'name', title: 'Top 5 exames', indexPrefix: true, flex: 2 },
        { key: 'value', title: 'Tempo Médio', align: 'right', flex: 1 },
      ],
    }
  },
  computed: {
    hasNtoData() {
      return (
        hasChartData(this.ntoAverageTime) ||
        hasChartData(this.topNtoExams, 'averageHours')
      )
    },
    hasSpecialtyData() {
      return (
        hasChartData(this.specialtyAverageTime) ||
        hasChartData(this.topSpecialtyExams, 'averageHours')
      )
    },
    ntoAverageTime() {
      return this.ntoAverageTimeByFilter[this.selectedNtoIndex] || []
    },
    specialtyAverageTime() {
      return this.specialtyAverageTimeByFilter[this.selectedSpecialtyIndex] || []
    },
    selectedSpecialtyName() {
      const name = this.specialtyFilters[this.selectedSpecialtyIndex]
      if (!name) return ''
      const normalizedName = name.toLocaleLowerCase('pt-BR')
      return normalizedName.charAt(0).toLocaleUpperCase('pt-BR') + normalizedName.slice(1)
    },
    topNtoExams() {
      return this.topNtoExamsByFilter[this.selectedNtoIndex] || []
    },
    topSpecialtyExams() {
      return this.topSpecialtyExamsByFilter[this.selectedSpecialtyIndex] || []
    },
  },
  watch: {
    period(value) {
      if (value) this.fetchDeliveryPerformance(value)
    },
  },
  created() {
    this.fetchDeliveryPerformance(this.period || new Date().toISOString().slice(0, 7))
  },
  methods: {
    async fetchDeliveryPerformance(period) {
      this.resetData()
      this.loading = true
      try {
        const { data } = await biDeliveryPerformance.getDeliveryPerformance(period)
        this.deliveryPerformanceResponse = data
        this.mapData(data)
      } catch (error) {
        console.error('[BI] Erro ao buscar performance de entrega:', error)
      } finally {
        this.loading = false
      }
    },
    resetData() {
      this.deliveryPerformanceResponse = null
      this.kpis = this.kpis.map(card => ({
        ...card,
        value: '--',
        complement: '',
        description: '',
        positive: false,
        negative: false,
      }))
      this.periods = []
      this.months = []
      this.referenceLineValue = 0
      this.deliveriesByDeadline = []
      this.stages = []
      this.colors = []
      this.ntoFilters = []
      this.ntoAverageTimeByFilter = []
      this.topNtoExamsByFilter = []
      this.specialtyFilters = []
      this.specialtyAverageTimeByFilter = []
      this.topSpecialtyExamsByFilter = []
      this.selectedNtoIndex = 0
      this.selectedSpecialtyIndex = 0
    },
    mapData(data) {
      const summary = data?.summary || {}
      const onTime = summary.onTimeOrEarly || {}
      const late = summary.late || {}
      const average = summary.avgTotalTime || {}
      const onTimeChange = onTime.changeVsPreviousMonth
      const lateChange = late.changeVsPreviousMonth
      const averageChange = average.changeVsPreviousMonth
      const hasOnTimeChange = Number.isFinite(onTimeChange)
      const hasLateChange = Number.isFinite(lateChange)
      const hasAverageChange = Number.isFinite(averageChange)

      this.kpis = [
        {
          title: 'No prazo ou Adiantados',
          value: this.formatPercentage(onTime.value),
          complement: hasOnTimeChange ? this.formatPercentageChange(onTimeChange) : '',
          complementVariant: !hasOnTimeChange
            ? ''
            : onTimeChange === 0
              ? 'neutral'
              : onTimeChange > 0
                ? 'green'
                : 'red',
          description: hasOnTimeChange ? 'vs. mês anterior' : '',
          positive: true,
        },
        {
          title: 'Atrasado',
          value: this.formatPercentage(late.value),
          complement: hasLateChange ? this.formatPercentageChange(lateChange) : '',
          complementVariant: !hasLateChange
            ? ''
            : lateChange === 0
              ? 'neutral'
              : lateChange < 0
                ? 'green'
                : 'red',
          description: hasLateChange ? 'vs. mês anterior' : '',
          negative: true,
        },
        {
          title: 'Tempo Médio Total',
          value: this.formatHours(average.value, average.hours, average.minutes),
          complement: hasAverageChange ? this.formatRelativeChange(averageChange) : '',
          complementVariant: !hasAverageChange
            ? ''
            : averageChange === 0
              ? 'neutral'
              : averageChange < 0
                ? 'green'
                : 'red',
          description: hasAverageChange ? 'vs. mês anterior' : '',
          valueColor: '#7F1A54',
          info: 'Considera a soma do Tempo Médio Logístico e do Tempo Médio de Análise.',
        },
      ]

      const deadlineItems = data?.charts?.deliveriesVsDeadlines?.items || []
      this.deliveriesByDeadline = deadlineItems.map(item => ({
        name: DEADLINE_LABELS[item.bucket] || item.bucket,
        value: item.quantity,
        percent: item.percentage,
        label: this.formatPercentage(item.percentage),
        detail: `(${Number(item.quantity).toLocaleString('pt-BR')} exames)`,
      }))
      this.colors = deadlineItems.map(item => DEADLINE_COLORS[item.bucket] || '#B6B6B6')

      const stageItems = data?.charts?.timeByStage?.items || []
      this.periods = stageItems.map(item => item.period)
      this.months = this.periods.map(period => this.formatMonth(period))
      this.referenceLineValue = data?.charts?.timeByStage?.currentAverageHours ?? 0
      this.stages = [
        {
          name: 'Tempo Médio Logístico',
          color: 'rgba(141, 208, 218, 1)',
          data: stageItems.map(item => item.logisticsHours),
        },
        {
          name: 'Tempo Médio de Análise',
          color: 'rgba(118, 157, 235, 1)',
          data: stageItems.map(item => item.analysisHours),
        },
      ]

      const ntoItems = data?.charts?.timeByNto?.items || []
      this.ntoFilters = ntoItems.map(item => item.description)
      this.ntoAverageTimeByFilter = ntoItems.map(item => this.mapSeries(item.series))
      this.topNtoExamsByFilter = ntoItems.map(item => this.mapTopExams(item.topExams))
      this.selectedNtoIndex = 0

      const specialtyItems = data?.charts?.timeBySpecialty?.items || []
      this.specialtyFilters = specialtyItems.map(item => item.description)
      this.specialtyAverageTimeByFilter = specialtyItems.map(item =>
        this.mapSeries(item.series)
      )
      this.topSpecialtyExamsByFilter = specialtyItems.map(item =>
        this.mapTopExams(item.topExams)
      )
      this.selectedSpecialtyIndex = 0

      if (data?.dataStatus?.updatedAt) {
        this.$emit('updated-at', data.dataStatus.updatedAt)
      }
    },
    mapSeries(series = []) {
      const valuesByPeriod = new Map(series.map(item => [item.period, item.averageHours]))
      return this.periods.map(period => valuesByPeriod.get(period) ?? null)
    },
    mapTopExams(exams = []) {
      return exams.map(exam => ({
        name: exam.description,
        value: this.formatHours(exam.averageHours),
        averageHours: exam.averageHours,
        sortValue: exam.quantity,
      }))
    },
    formatPercentage(value) {
      if (!Number.isFinite(value)) return '--'
      return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    },
    formatPercentageChange(value) {
      const sign = value >= 0 ? '+' : '-'
      const amount = Math.abs(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return `${sign}${amount}%`
    },
    formatRelativeChange(value) {
      const sign = value >= 0 ? '+' : '-'
      const amount = (Math.abs(value) * 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return `${sign}${amount}%`
    },
    formatHours(value, hours, minutes) {
      if (!Number.isFinite(value)) return '--'
      const totalMinutes =
        Number.isFinite(hours) && Number.isFinite(minutes)
          ? Math.round(hours * 60 + minutes)
          : Math.round(value * 60)
      const sign = totalMinutes < 0 ? '-' : ''
      const absoluteMinutes = Math.abs(totalMinutes)
      const formattedHours = Math.floor(absoluteMinutes / 60)
      const formattedMinutes = absoluteMinutes % 60
      return formattedMinutes
        ? `${sign}${formattedHours} h ${formattedMinutes} min`
        : `${sign}${formattedHours} h`
    },
    formatMonth(period) {
      const [year, month] = period.split('-')
      return `${MONTHS[Number(month) - 1]}/${year.slice(-2)}`
    },
  },
}
</script>

<style scoped lang="scss">
.delivery-performance {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 24px;
  font-family: 'Roboto', sans-serif;

  &__cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__grid {
    display: flex;
    gap: 16px;

    &--top {
      // grid com as mesmas colunas do &__cards, garante alinhamento exato com os cards acima
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    &--average-time {
      gap: 64px;

      > div {
        flex: 1;
      }
    }
  }

  &__cards > * {
    min-height: 88px;
    min-width: 0;
  }

  &__deadline-kpis {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    min-width: 0;

    > * {
      min-width: 0;
    }
  }

  &__grid--top > * {
    min-height: 214px;
    min-width: 0;
  }

  &__panel {
    display: flex;
    flex-direction: column;
    padding: 16px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  }

  &__panel-title {
    margin-bottom: 16px;
    color: rgba(70, 70, 70, 1);
    font-size: 16px;
    font-weight: 700;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__filters button {
    height: inherit;
    padding: 8px 16px;
    border: 1px solid #7f1a54;
    border-radius: 4px;
    background: #fff;
    color: #7f1a54;
    font-size: 11.11px;
    font-family: 'Exo 2', sans-serif;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(.delivery-performance__filter--active) {
      background: #fff;
      border-color: rgba(236, 32, 114, 1);
      color: rgba(236, 32, 114, 1);
    }
  }

  &__filters .delivery-performance__filter--active {
    background: #7f1a54;
    color: #fff;
  }

  &__grid--detail > * {
    min-width: 0;
  }

  @media (max-width: 768px) {
    &__cards,
    &__grid--top,
    &__deadline-kpis {
      grid-template-columns: 1fr;
    }

    &__grid {
      flex-direction: column;
    }
  }
}
</style>
